import React, { useState } from 'react';
import { Music, ArrowLeft, PlayCircle, Image as ImageIcon } from 'lucide-react';

export default function App() {
  const [currentPage, setCurrentPage] = useState('home');

  const concerti = [
    {
      id: 'concerto-1',
      title: 'Timpani Concerto No. 1: The Olympian',
      shortDesc: 'World Premiere by Paul Yancich and The Cleveland Orchestra, Christoph von Dohnányi conducting.',
      // EDIT THESE TWO LINES:
      imageUrl: '/OLYMPIAN_SAMPLE_IMAGE.png', 
      vimeoEmbedUrl: 'https://player.vimeo.com/video/127409415',
      
      fullText: (
        <>
          <p className="mb-4">
            "The Olympian" has been acknowledged as a "rite of passage" for modern solo
            timpanists to undertake. Numerous performances (in both symphony orchestra and symphonic wind
            ensemble versions) have been given since its premiere in 1990.
          </p>
          <p>
             Check out this brief documentary celebrating the 25th Anniversary of “The Olympian"
          </p>
        </>
      )
    },
    {
      id: 'concerto-2',
      title: 'DYNASTY Double Timpani Concerto',
      shortDesc: 'A tour de force for two master timpani soloists together in front of the orchestra.',
      imageUrl: 'DYNASTY_ASO_CD.png', 
      vimeoEmbedUrl: 'https://player.vimeo.com/video/1222525123',
      fullText: (
        <p className="mb-4">
          The DYNASTY Double Concerto (aka Double D) received performances by both the Atlanta Symphony and the Cleveland Orchestra in its World Premiere season. Subsequently it has been performed in both orchestral and symphonic wind versions, including its Carnegie Hall premiere in 2018. Check out this brief overview video featuring the Brothers Yancich. For links to the entire piece, please contact the composer at james@jamesoliverio.com
        </p>
      )
    },
     {
      id: 'concerto-3',
      title: 'Legacy Ascendant: Concerto for Timpani & Strings',
      shortDesc: '“Legacy Ascendant” received its World Premiere with Soloist Paul Yancich and the Cleveland Orchestra. It is now scheduled for its European premiere with Soloist Stephan Cürlis and the NDR Elbphilharmonie Orchestra under the baton of Alan Gilbert on September 4 and 5, 2026.',
      imageUrl: 'LEGACY_Opening_NIght_YouTube.png', 
      vimeoEmbedUrl: 'https://www.youtube.com/embed/KENrplP8ZfQ', 
      fullText: (
        <div className="mb-4">
          <p className="mb-4">
            This special concert marks the opening week of the 2026/2027 season and features the orchestra's legendary solo timpanist, Stephan Cürlis, performing a showcase concerto by composer James Oliverio as Cürlis begins his final farewell season before retirement.
          </p>
          <p className="mb-2 font-semibold">
            Upcoming European Premiere (Sept 4 & 5, 2026):
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <a 
                href="https://www.elbphilharmonie.de/en/whats-on/ndr-elbphilharmonie-orchestra-stephan-curlis-alan-gilbert/27821" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                View Event Details & Tickets at the Elbphilharmonie
              </a>
            </li>
            <li>
              <a 
                href="https://www.youtube.com/watch?v=KENrplP8ZfQ" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Bookmark the Sept 5 Livestream on YouTube
              </a>
            </li>
          </ul>
        </div>
      )
    }
  ];

  const renderHome = () => (
    <div className="max-w-5xl mx-auto p-6">
      <header className="mb-12 text-center">
        <h1 className="text-4xl font-bold mb-4 text-gray-900">Welcome to Timpani Concerto.com</h1>
        <p className="text-lg text-gray-600">This month featuring the works of American composer James Oliverio</p>
      </header>
      
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {concerti.map(concerto => (
          <div key={concerto.id} className="border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white flex flex-col">
            
            {/* Card Image Preview */}
            {concerto.imageUrl ? (
              <img 
                src={concerto.imageUrl} 
                alt={concerto.title} 
                onClick={() => setCurrentPage(concerto.id)}
                className="w-full h-48 object-cover border-b cursor-pointer"
              />
            ) : (
              <div 
                onClick={() => setCurrentPage(concerto.id)}
                className="w-full h-48 bg-gray-100 flex flex-col items-center justify-center border-b text-gray-400 cursor-pointer"
              >
                <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                <span className="text-sm font-medium">No Image Yet</span>
              </div>
            )}
            
            <div className="p-6 flex flex-col flex-grow">
              <div className="flex items-start mb-4">
                <h2 className="text-xl font-semibold text-gray-900 leading-tight">{concerto.title}</h2>
              </div>
              <p className="text-gray-600 mb-6 flex-grow text-sm">{concerto.shortDesc}</p>
              <button
                onClick={() => setCurrentPage(concerto.id)}
                className="text-blue-600 font-medium hover:text-blue-800 flex items-center w-fit bg-blue-50 px-4 py-2 rounded-lg transition-colors"
              >
                Read More <PlayCircle className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDetail = () => {
    const concerto = concerti.find(c => c.id === currentPage);
    if (!concerto) return null;

    return (
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={() => setCurrentPage('home')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-8 font-medium transition-colors bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200 w-fit"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </button>
        
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          
          {/* Detail Page Media: Shows Video if available, otherwise shows Image, otherwise shows Placeholder */}
          {concerto.vimeoEmbedUrl ? (
            <div className="w-full aspect-video bg-black">
              <iframe
                src={concerto.vimeoEmbedUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={concerto.title}
              ></iframe>
            </div>
          ) : concerto.imageUrl ? (
            <img 
              src={concerto.imageUrl} 
              alt={concerto.title} 
              className="w-full h-72 object-cover border-b"
            />
          ) : (
            <div className="w-full h-72 bg-slate-100 flex flex-col items-center justify-center border-b text-slate-400">
              <ImageIcon className="w-16 h-16 mb-3 opacity-50" />
              <span className="text-lg font-medium">No Media Available</span>
            </div>
          )}

          <div className="p-8 md:p-10">
            <div className="flex items-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">{concerto.title}</h1>
            </div>

            <div className="prose max-w-none text-gray-700 leading-relaxed text-lg">
              {concerto.fullText}
            </div>
          </div>
        </article>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 font-sans">
      {currentPage === 'home' ? renderHome() : renderDetail()}
    </div>
  );
}